import { useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import { sendGTMEvent } from "@next/third-parties/google";
import cookie from "cookie";
import { isBrowser } from "../utils";
import { useStore } from "../../store";
import { CookieConsentStatus } from "../../store/store.types";
import {
  ImpactShopProductType,
  ImpactShopBasketProduct,
  ImpactShopProductPrice,
} from "@ecologi/shared";

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_KEY;
export const GTM_AUTH = process.env.NEXT_PUBLIC_GTM_AUTH;
export const GTM_PREVIEW = process.env.NEXT_PUBLIC_GTM_PREVIEW;

const COOKIE_CONSENT_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_CONSENT_DOMAIN;

export const pageview = () => {
  sendGTMEvent({
    event: "pageview",
    page: window.location.pathname,
  });
};

export const pushToDataLayer = (payload: object) => {
  sendGTMEvent(payload);
};

export const pushLeadGenSuccess = () => {
  sendGTMEvent({
    event: "leadGenFormSubmissionSuccess",
  });
};

// --- GA4 Ecommerce ---

export type GA4Item = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_brand?: string;
};

const getPrice = (prices: ImpactShopProductPrice[], currency: string) => {
  const match = prices.find((p) => p.currency === currency);
  if (!match) console.warn(`[GTM] No price found for currency: ${currency}`);
  return (match?.amount ?? 0) / 100;
};

export const toGA4Item = (
  product: ImpactShopProductType | ImpactShopBasketProduct,
  currency: string,
  quantity?: number
): GA4Item => ({
  item_id: product.slug,
  item_name: product.display.title,
  price: getPrice(product.prices, currency),
  quantity: quantity ?? ("quantity" in product ? product.quantity : 1),
  item_category: product.productType,
  item_brand: "Ecologi",
});

const clearAndSend = (payload: object) => {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent(payload);
};

export const pushViewItemList = (items: GA4Item[], currency: string) => {
  clearAndSend({
    event: "view_item_list",
    ecommerce: {
      item_list_name: "Impact Shop",
      currency,
      items,
    },
  });
};

export const pushViewItem = (
  item: GA4Item,
  currency: string,
  value: number
) => {
  clearAndSend({
    event: "view_item",
    ecommerce: {
      currency,
      value,
      items: [item],
    },
  });
};

export const pushViewCart = (
  items: GA4Item[],
  currency: string,
  value: number
) => {
  clearAndSend({
    event: "view_cart",
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const pushSelectItem = (item: GA4Item, currency: string) => {
  clearAndSend({
    event: "select_item",
    ecommerce: {
      item_list_name: "Impact Shop",
      currency,
      items: [item],
    },
  });
};

export const pushAddToCart = (
  items: GA4Item[],
  currency: string,
  value: number
) => {
  clearAndSend({
    event: "add_to_cart",
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const pushRemoveFromCart = (
  items: GA4Item[],
  currency: string,
  value: number
) => {
  clearAndSend({
    event: "remove_from_cart",
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const pushBeginCheckout = (
  items: GA4Item[],
  currency: string,
  value: number,
  checkoutType: "logged_in" | "guest"
) => {
  clearAndSend({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value,
      items,
    },
    checkout_type: checkoutType,
  });
};

export const pushPurchase = (data: {
  transaction_id: string;
  value: number;
  currency: string;
  items: GA4Item[];
  subscription_type?: string;
  user_id?: string;
  referral_source?: string;
}) => {
  clearAndSend({
    event: "purchase",
    ecommerce: {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      items: data.items,
    },
    ...(data.subscription_type && {
      subscription_type: data.subscription_type,
    }),
    ...(data.user_id && { user_id: data.user_id }),
    ...(data.referral_source && { referral_source: data.referral_source }),
  });
};

export function useFireOnce(
  callback: () => void,
  ready: boolean,
  deps: unknown[]
) {
  const fired = useRef(false);
  useEffect(() => {
    if (!fired.current && ready) {
      fired.current = true;
      callback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useTrackPageViews() {
  const router = useRouter();

  useEffect(() => {
    // Track a page view event on initial load
    if (getEcologiConsentCookie()) {
      pageview();
    }

    // Track a page view event on every page change
    // and remove on unmount
    router.events.on("routeChangeComplete", pageview);
    return () => {
      router.events.off("routeChangeComplete", pageview);
    };
  }, [router.events]);
}

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
/**
 * Note, this value must be kept in-sync with the cookie consent name defined
 * in...
 * The Google Tag Manager (GTM) Consent settings
 * The Framer cookie banner implementation
 * Zero: https://github.com/ecologi/mono/blob/main/apps/zero-client/src/config/index.ts#L11
 */
const COOKIE_NAME = "ecologi_cookie_consent_20220224";

const setEcologiConsentCookie = (value: string) => {
  document.cookie = cookie.serialize(COOKIE_NAME, value, {
    maxAge: ONE_YEAR_SECONDS,
    domain: COOKIE_CONSENT_DOMAIN,
  });
};

const getEcologiConsentCookie = () => {
  const cookies = cookie.parse(document.cookie);
  return cookies[COOKIE_NAME];
};

/**
 * TODO: Move CookieConsentProps type to shared package
 *
 * import { CookieConsentProps } from "@ecologi/react-components";
 */
export function useCookieConsent(): any {
  const { cookieConsentStatus, setCookieConsent } = useStore();

  useEffect(() => {
    if (!isBrowser()) return;

    if (!getEcologiConsentCookie()) {
      setCookieConsent(CookieConsentStatus.CONSENT_REQUESTED);
    }
  }, [setCookieConsent]);

  const onReject = useCallback(() => {
    setEcologiConsentCookie("false");
    setCookieConsent(CookieConsentStatus.REJECTED);
  }, [setCookieConsent]);

  const onAccept = useCallback(() => {
    setEcologiConsentCookie("true");
    setCookieConsent(CookieConsentStatus.ACCEPTED);
    pageview();
  }, [setCookieConsent]);

  return {
    onReject,
    onAccept,
    isOpen: cookieConsentStatus === CookieConsentStatus.CONSENT_REQUESTED,
  };
}
